import { useState, useEffect } from "react";
import {
  X,
  AlertTriangle,
  Loader2,
  Banknote,
  Smartphone,
  CreditCard,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useBillingSessionStore } from "../../stores/billingSessionStore";
import { useAuthStore } from "../../stores/authStore";
import { counterService, Counter } from "../../services/counter.service";
import {
  billingSessionService,
  HandoverCandidate,
  UnresolvedSessionPayment,
} from "../../services/billing-session.service";
import { showToast } from "../../lib/toast";
import { formatDateTime } from "../../lib/utils";

// Cash denomination structure for Indian currency
interface CashDenomination {
  notes2000: number;
  notes500: number;
  notes200: number;
  notes100: number;
  notes50: number;
  notes20: number;
  notes10: number;
  coins: number;
}

const DENOMINATIONS = [
  {
    key: "notes2000",
    label: "₹2000",
    value: 2000,
    color: "bg-pink-50 border-pink-200",
  },
  {
    key: "notes500",
    label: "₹500",
    value: 500,
    color: "bg-purple-50 border-purple-200",
  },
  {
    key: "notes200",
    label: "₹200",
    value: 200,
    color: "bg-yellow-50 border-yellow-200",
  },
  {
    key: "notes100",
    label: "₹100",
    value: 100,
    color: "bg-green-50 border-green-200",
  },
  {
    key: "notes50",
    label: "₹50",
    value: 50,
    color: "bg-teal-50 border-teal-200",
  },
  {
    key: "notes20",
    label: "₹20",
    value: 20,
    color: "bg-orange-50 border-orange-200",
  },
  {
    key: "notes10",
    label: "₹10",
    value: 10,
    color: "bg-blue-50 border-blue-200",
  },
  {
    key: "coins",
    label: "Coins",
    value: 1,
    color: "bg-gray-50 border-gray-200",
  },
] as const;

const initialDenomination: CashDenomination = {
  notes2000: 0,
  notes500: 0,
  notes200: 0,
  notes100: 0,
  notes50: 0,
  notes20: 0,
  notes10: 0,
  coins: 0,
};

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuid(value: string | undefined): value is string {
  return Boolean(value && UUID_PATTERN.test(value));
}

function getRequestErrorMessage(error: unknown, fallback: string): string {
  const requestError = error as {
    message?: string;
    stableError?: { message?: string };
    response?: {
      data?: {
        message?: string | string[];
        errors?: string[];
      };
    };
  };
  const responseData = requestError?.response?.data;

  if (Array.isArray(responseData?.errors) && responseData.errors.length > 0) {
    return responseData.errors.join(", ");
  }

  if (Array.isArray(responseData?.message)) {
    return responseData.message.join(", ");
  }

  return (
    responseData?.message ||
    requestError?.stableError?.message ||
    requestError?.message ||
    fallback
  );
}

function isConflictResponse(error: unknown): boolean {
  return (
    (error as { response?: { status?: number } })?.response?.status === 409
  );
}

// Reusable denomination input component
interface DenominationInputProps {
  denom: (typeof DENOMINATIONS)[number];
  value: number;
  onChange: (value: string) => void;
  ringColor: string;
}

function DenominationInput({
  denom,
  value,
  onChange,
  ringColor,
}: DenominationInputProps) {
  return (
    <div className={`p-2 rounded-lg border ${denom.color}`}>
      <div className="text-xs font-medium text-gray-600 mb-1 text-center">
        {denom.label}
      </div>
      <input
        type="number"
        min="0"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="0"
        className={`w-full px-2 py-1.5 text-center text-sm font-medium border border-gray-200 rounded focus:outline-none focus:ring-1 ${ringColor} bg-white`}
      />
      {denom.key !== "coins" && value > 0 && (
        <div className="text-xs text-gray-500 text-center mt-1">
          = ₹{(value * denom.value).toLocaleString()}
        </div>
      )}
    </div>
  );
}

// Denomination Grid Component
interface DenominationGridProps {
  denomination: CashDenomination;
  onChange: (key: keyof CashDenomination, value: string) => void;
  ringColor: string;
}

function DenominationGrid({
  denomination,
  onChange,
  ringColor,
}: DenominationGridProps) {
  return (
    <>
      {/* Row 1: ₹2000, ₹500, ₹200 */}
      <div className="grid grid-cols-3 gap-2 mb-2">
        {DENOMINATIONS.slice(0, 3).map((denom) => (
          <DenominationInput
            key={denom.key}
            denom={denom}
            value={denomination[denom.key as keyof CashDenomination]}
            onChange={(val) =>
              onChange(denom.key as keyof CashDenomination, val)
            }
            ringColor={ringColor}
          />
        ))}
      </div>
      {/* Row 2: ₹100, ₹50, ₹20 */}
      <div className="grid grid-cols-3 gap-2 mb-2">
        {DENOMINATIONS.slice(3, 6).map((denom) => (
          <DenominationInput
            key={denom.key}
            denom={denom}
            value={denomination[denom.key as keyof CashDenomination]}
            onChange={(val) =>
              onChange(denom.key as keyof CashDenomination, val)
            }
            ringColor={ringColor}
          />
        ))}
      </div>
      {/* Row 3: ₹10, Coins (centered) */}
      <div className="flex justify-center gap-2">
        {DENOMINATIONS.slice(6).map((denom) => (
          <div key={denom.key} className="w-1/3">
            <DenominationInput
              denom={denom}
              value={denomination[denom.key as keyof CashDenomination]}
              onChange={(val) =>
                onChange(denom.key as keyof CashDenomination, val)
              }
              ringColor={ringColor}
            />
          </div>
        ))}
      </div>
    </>
  );
}

interface OpenSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  storeId: string;
  storeName?: string;
  onSessionOpened?: (storeId: string, counterId: string) => void;
}

export function OpenSessionModal({
  isOpen,
  onClose,
  storeId,
  storeName,
  onSessionOpened,
}: OpenSessionModalProps) {
  const { openSession, fetchActiveSession, isLoading } =
    useBillingSessionStore();
  const { setSelectedStore, setSelectedCounter } = useAuthStore();
  const [counters, setCounters] = useState<Counter[]>([]);
  const [counterId, setCounterId] = useState("");
  const [shift, setShift] = useState<
    "MORNING" | "AFTERNOON" | "EVENING" | "NIGHT"
  >("MORNING");
  const [loadingCounters, setLoadingCounters] = useState(false);
  const [denomination, setDenomination] =
    useState<CashDenomination>(initialDenomination);
  const [showDenomination, setShowDenomination] = useState(false);

  useEffect(() => {
    if (isOpen && storeId) {
      loadCounters();
    }
  }, [isOpen, storeId]);

  const loadCounters = async () => {
    setLoadingCounters(true);
    try {
      const response = await counterService.getCounters({
        page: 1,
        limit: 100,
        storeId,
      });
      setCounters(response.data || []);
      if (response.data?.length === 1) {
        setCounterId(response.data[0].id);
      }
    } catch {
      setCounters([]);
    } finally {
      setLoadingCounters(false);
    }
  };

  const calculateTotal = () => {
    return (
      denomination.notes2000 * 2000 +
      denomination.notes500 * 500 +
      denomination.notes200 * 200 +
      denomination.notes100 * 100 +
      denomination.notes50 * 50 +
      denomination.notes20 * 20 +
      denomination.notes10 * 10 +
      denomination.coins
    );
  };

  const handleDenominationChange = (
    key: keyof CashDenomination,
    value: string,
  ) => {
    const numValue = parseInt(value) || 0;
    setDenomination((prev) => ({ ...prev, [key]: numValue }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!counterId) {
      showToast.error("Please select a counter");
      return;
    }

    const selectedCounter = counters.find(
      (counter) => counter.id === counterId,
    );
    if (!selectedCounter) {
      showToast.error("Please select a valid counter");
      return;
    }

    // The persisted store selection can be stale. The selected counter was
    // loaded from the API, so its store ID is the canonical value to submit.
    const sessionStoreId = isUuid(selectedCounter.storeId)
      ? selectedCounter.storeId
      : isUuid(storeId)
        ? storeId
        : null;
    if (!sessionStoreId) {
      showToast.error(
        "Selected store has an invalid identifier. Refresh the page and try again.",
      );
      return;
    }

    try {
      await openSession({
        storeId: sessionStoreId,
        counterId,
        shift,
        openingCash: calculateTotal(),
        denominations: [
          { denominationPaise: 200_000, count: denomination.notes2000 },
          { denominationPaise: 50_000, count: denomination.notes500 },
          { denominationPaise: 20_000, count: denomination.notes200 },
          { denominationPaise: 10_000, count: denomination.notes100 },
          { denominationPaise: 5_000, count: denomination.notes50 },
          { denominationPaise: 2_000, count: denomination.notes20 },
          { denominationPaise: 1_000, count: denomination.notes10 },
          { denominationPaise: 100, count: denomination.coins },
        ],
      });
      if (sessionStoreId !== storeId) {
        await setSelectedStore(sessionStoreId);
      }
      setSelectedCounter(counterId);
      showToast.success("Billing session opened successfully");
      onSessionOpened?.(sessionStoreId, counterId);
      handleClose();
    } catch (error: unknown) {
      if (isConflictResponse(error)) {
        const existingSession = await fetchActiveSession(
          sessionStoreId,
          counterId,
        );
        if (existingSession?.id && existingSession.counterId) {
          if (sessionStoreId !== storeId) {
            await setSelectedStore(sessionStoreId);
          }
          setSelectedCounter(existingSession.counterId);
          showToast.success("Existing billing session restored");
          onSessionOpened?.(sessionStoreId, existingSession.counterId);
          handleClose();
          return;
        }
      }
      showToast.error(getRequestErrorMessage(error, "Failed to open session"));
    }
  };

  const handleClose = () => {
    setCounterId("");
    setShift("MORNING");
    setDenomination(initialDenomination);
    setShowDenomination(false);
    onClose();
  };

  if (!isOpen) return null;

  const totalCash = calculateTotal();

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header - Fixed */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-teal-500 to-teal-600 flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-white">
              Open Billing Session
            </h2>
            {storeName && (
              <p className="text-sm text-teal-100 mt-0.5">Store: {storeName}</p>
            )}
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body - Scrollable */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Counter & Shift Row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Counter <span className="text-red-500">*</span>
              </label>
              {loadingCounters ? (
                <div className="flex items-center justify-center py-2.5 text-gray-400 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                </div>
              ) : counters.length === 0 ? (
                <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded-lg">
                  No counters
                </div>
              ) : (
                <select
                  value={counterId}
                  onChange={(e) => setCounterId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  required
                >
                  <option value="">Select</option>
                  {counters.map((counter) => (
                    <option key={counter.id} value={counter.id}>
                      {counter.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Shift
              </label>
              <select
                value={shift}
                onChange={(e) => setShift(e.target.value as typeof shift)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
              >
                <option value="MORNING">Morning</option>
                <option value="AFTERNOON">Afternoon</option>
                <option value="EVENING">Evening</option>
                <option value="NIGHT">Night</option>
              </select>
            </div>
          </div>

          {/* Opening Cash Section - Collapsible */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setShowDenomination(!showDenomination)}
              className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Banknote className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-gray-700">
                  Opening Cash
                </span>
                {totalCash > 0 && (
                  <span className="text-sm font-bold text-green-600 ml-2">
                    ₹{totalCash.toLocaleString()}
                  </span>
                )}
              </div>
              {showDenomination ? (
                <ChevronUp className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-500" />
              )}
            </button>
            {showDenomination && (
              <div className="p-4 border-t border-gray-200">
                <DenominationGrid
                  denomination={denomination}
                  onChange={handleDenominationChange}
                  ringColor="ring-teal-500"
                />
                {totalCash > 0 && (
                  <div className="mt-3 bg-green-50 rounded-lg p-3 border border-green-200">
                    <div className="flex justify-between items-center">
                      <span className="text-green-700 font-medium text-sm">
                        Total
                      </span>
                      <span className="text-xl font-bold text-green-700">
                        ₹{totalCash.toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer - Fixed */}
        <div className="px-5 py-4 border-t border-gray-100 bg-white flex-shrink-0">
          <form onSubmit={handleSubmit} className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !counterId || counters.length === 0}
              className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              Open Session
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

interface CloseSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSessionClosed?: () => void;
}

export function CloseSessionModal({
  isOpen,
  onClose,
  onSessionClosed,
}: CloseSessionModalProps) {
  const { activeSession, closeSession, isLoading } = useBillingSessionStore();
  const [notes, setNotes] = useState("");
  const [action, setAction] = useState<"CLOSE" | "HANDOVER">("CLOSE");
  const [handoverTo, setHandoverTo] = useState("");
  const [approvalRequestId, setApprovalRequestId] = useState("");
  const [candidates, setCandidates] = useState<HandoverCandidate[]>([]);
  const [unresolvedPayments, setUnresolvedPayments] = useState<
    UnresolvedSessionPayment[]
  >([]);
  const [resolvingPaymentId, setResolvingPaymentId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [denomination, setDenomination] =
    useState<CashDenomination>(initialDenomination);

  useEffect(() => {
    if (!isOpen || !activeSession) return;
    void billingSessionService
      .getHandoverCandidates(activeSession.id)
      .then(setCandidates)
      .catch(() => setCandidates([]));
    void billingSessionService
      .getUnresolvedPayments(activeSession.id)
      .then(setUnresolvedPayments)
      .catch(() => setUnresolvedPayments([]));
  }, [isOpen, activeSession?.id]);

  const refreshUnresolvedPayments = async () => {
    if (!activeSession) return;
    setUnresolvedPayments(
      await billingSessionService.getUnresolvedPayments(activeSession.id),
    );
  };

  const handlePaymentResolution = async (
    attempt: UnresolvedSessionPayment,
    resolution:
      | "CANCEL_PENDING"
      | "CANCEL_AUTHORIZATION"
      | "CAPTURE_AUTHORIZATION"
      | "RESOLVE_EXCESS_COLLECTION",
  ) => {
    if (!activeSession) return;
    if (resolution === "RESOLVE_EXCESS_COLLECTION") {
      if (!attempt.customerId) {
        showToast.error(
          "Assign a customer before moving excess funds to credit",
        );
        return;
      }
      if (!approvalRequestId.trim()) {
        showToast.error(
          "Enter the approved request ID to resolve excess funds",
        );
        return;
      }
    }
    try {
      setResolvingPaymentId(attempt.id);
      if (resolution === "CAPTURE_AUTHORIZATION")
        await billingSessionService.captureAuthorization(attempt.id);
      else if (resolution === "RESOLVE_EXCESS_COLLECTION")
        await billingSessionService.resolveExcessCollection(
          attempt.id,
          attempt.customerId!,
          approvalRequestId.trim(),
        );
      else
        await billingSessionService.resolveSessionPayment(
          activeSession.id,
          attempt.id,
          resolution,
        );
      await refreshUnresolvedPayments();
      showToast.success("Payment attempt resolved");
    } catch (error: any) {
      showToast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to resolve payment attempt",
      );
    } finally {
      setResolvingPaymentId("");
    }
  };

  // Calculate expected cash
  const expectedCash = activeSession
    ? (activeSession.expectedCash ??
      Number(activeSession.openingCash || 0) +
        Number(activeSession.totalCashIn || 0) -
        Number(activeSession.totalCashOut || 0))
    : 0;

  const expectedUPI = activeSession ? Number(activeSession.totalUPI || 0) : 0;
  const expectedCard = activeSession ? Number(activeSession.totalCard || 0) : 0;

  const calculateCashTotal = () => {
    return (
      denomination.notes2000 * 2000 +
      denomination.notes500 * 500 +
      denomination.notes200 * 200 +
      denomination.notes100 * 100 +
      denomination.notes50 * 50 +
      denomination.notes20 * 20 +
      denomination.notes10 * 10 +
      denomination.coins
    );
  };

  const handleDenominationChange = (
    key: keyof CashDenomination,
    value: string,
  ) => {
    const numValue = parseInt(value) || 0;
    setDenomination((prev) => ({ ...prev, [key]: numValue }));
  };

  const hasDenominationEntered = () => {
    return Object.values(denomination).some((v) => v > 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeSession) return;

    // Only require denomination if there's expected cash
    if (expectedCash > 0 && !hasDenominationEntered()) {
      showToast.error("Please enter cash denomination details");
      return;
    }

    const actualCash = calculateCashTotal();
    if (action === "HANDOVER" && !handoverTo) {
      showToast.error("Select the cashier receiving the drawer");
      return;
    }

    const denominations = [
      { denominationPaise: 200_000, count: denomination.notes2000 },
      { denominationPaise: 50_000, count: denomination.notes500 },
      { denominationPaise: 20_000, count: denomination.notes200 },
      { denominationPaise: 10_000, count: denomination.notes100 },
      { denominationPaise: 5_000, count: denomination.notes50 },
      { denominationPaise: 2_000, count: denomination.notes20 },
      { denominationPaise: 1_000, count: denomination.notes10 },
      { denominationPaise: 100, count: denomination.coins },
    ];

    try {
      setSubmitting(true);
      if (action === "HANDOVER")
        await billingSessionService.handoverSession(activeSession.id, {
          countedCashPaise: Math.round(actualCash * 100),
          denominations,
          handoverTo,
          reasonCode: notes.trim() || "SHIFT_HANDOVER",
          ...(approvalRequestId.trim()
            ? { approvalRequestId: approvalRequestId.trim() }
            : {}),
        });
      else
        await closeSession(activeSession.id, {
          actualCash,
          notes: notes || undefined,
          denominations,
          ...(actualCash !== expectedCash
            ? { reasonCode: "OPERATOR_REPORTED_VARIANCE" }
            : {}),
          ...(approvalRequestId.trim()
            ? { approvalRequestId: approvalRequestId.trim() }
            : {}),
        });

      const cashVariance = actualCash - expectedCash;
      if (action === "HANDOVER") {
        showToast.success("Drawer handed over successfully");
      } else if (expectedCash > 0 && Math.abs(cashVariance) > 0) {
        showToast.success(
          `Session closed with ₹${Math.abs(cashVariance).toLocaleString()} cash ${cashVariance > 0 ? "surplus" : "shortage"}`,
        );
      } else {
        showToast.success("Session closed successfully!");
      }
      onSessionClosed?.();
      handleClose();
    } catch (error: any) {
      if (Array.isArray(error.response?.data?.attempts))
        setUnresolvedPayments(error.response.data.attempts);
      showToast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to review session",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setDenomination(initialDenomination);
    setNotes("");
    setAction("CLOSE");
    setHandoverTo("");
    setApprovalRequestId("");
    setCandidates([]);
    setUnresolvedPayments([]);
    setResolvingPaymentId("");
    onClose();
  };

  if (!isOpen || !activeSession) return null;

  const actualCash = calculateCashTotal();
  const cashVariance = actualCash - expectedCash;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header - Fixed */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-red-500 to-red-600 flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-white">
              Close Billing Session
            </h2>
            <p className="text-sm text-red-100 mt-0.5">
              #{activeSession.sessionNumber} • {activeSession.counter?.name}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body - Scrollable */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="grid grid-cols-2 gap-2 rounded-xl bg-gray-100 p-1">
            {(["CLOSE", "HANDOVER"] as const).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setAction(value)}
                className={`rounded-lg px-3 py-2 text-sm font-semibold ${
                  action === value
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500"
                }`}
              >
                {value === "CLOSE" ? "Close shift" : "Handover drawer"}
              </button>
            ))}
          </div>

          {action === "CLOSE" && unresolvedPayments.length > 0 && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
              <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-bold text-amber-900">
                    Payments requiring review
                  </h3>
                  <p className="mt-1 text-xs text-amber-800">
                    Resolve each attempt before closing. Provider success events
                    received later are still reconciled by the server.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    void refreshUnresolvedPayments().catch(() =>
                      showToast.error("Unable to refresh payment attempts"),
                    )
                  }
                  className="rounded-lg border border-amber-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-amber-900"
                >
                  Retry
                </button>
              </div>
              <div className="space-y-2">
                {unresolvedPayments.map((attempt) => (
                  <div
                    key={attempt.id}
                    className="rounded-lg border border-amber-200 bg-white p-3"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">
                          {attempt.method.replace(/_/g, " ")} ·{" "}
                          {attempt.status.replace(/_/g, " ")}
                        </div>
                        <div className="text-xs text-gray-500">
                          ₹
                          {(Number(attempt.appliedPaise) / 100).toLocaleString(
                            "en-IN",
                            { minimumFractionDigits: 2 },
                          )}
                          {attempt.providerReference
                            ? ` · Ref ${attempt.providerReference}`
                            : ""}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {attempt.allowedActions.map((resolution) => (
                          <button
                            key={resolution}
                            type="button"
                            disabled={resolvingPaymentId === attempt.id}
                            onClick={() =>
                              void handlePaymentResolution(attempt, resolution)
                            }
                            className="rounded-lg bg-amber-700 px-2.5 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                          >
                            {resolvingPaymentId === attempt.id
                              ? "Working…"
                              : resolution === "CAPTURE_AUTHORIZATION"
                                ? "Complete"
                                : resolution === "RESOLVE_EXCESS_COLLECTION"
                                  ? "Move to customer credit"
                                  : resolution === "CANCEL_AUTHORIZATION"
                                    ? "Cancel authorization"
                                    : "Cancel pending"}
                          </button>
                        ))}
                      </div>
                    </div>
                    {attempt.status === "EXCESS_COLLECTION" && (
                      <p className="mt-2 text-xs text-amber-800">
                        A manager approval request ID is required below.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {action === "HANDOVER" && (
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">
                Receiving cashier <span className="text-red-500">*</span>
              </label>
              <select
                value={handoverTo}
                onChange={(event) => setHandoverTo(event.target.value)}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
              >
                <option value="">Select an eligible cashier</option>
                {candidates.map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.firstName} {candidate.lastName} (
                    {candidate.email})
                  </option>
                ))}
              </select>
            </div>
          )}
          {/* Session Summary */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Session Summary
            </h3>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="text-center p-2 bg-white rounded-lg">
                <div className="text-gray-500 text-xs">Total Sales</div>
                <div className="font-bold text-green-600">
                  ₹{Number(activeSession.totalSales || 0).toLocaleString()}
                </div>
              </div>
              <div className="text-center p-2 bg-white rounded-lg">
                <div className="text-gray-500 text-xs">Invoices</div>
                <div className="font-bold">
                  {activeSession.invoiceCount || 0}
                </div>
              </div>
              <div className="text-center p-2 bg-white rounded-lg">
                <div className="text-gray-500 text-xs">Duration</div>
                <div className="font-bold text-gray-700">
                  {Math.round(
                    (Date.now() - new Date(activeSession.startTime).getTime()) /
                      (1000 * 60 * 60),
                  )}
                  h
                </div>
              </div>
            </div>
          </div>

          {/* Digital Payments (Read-only) */}
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
            <h3 className="text-sm font-semibold text-blue-700 mb-2">
              Digital Payments (Deposited to Bank)
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 bg-white rounded-lg p-3">
                <Smartphone className="w-4 h-4 text-purple-600" />
                <div>
                  <div className="text-xs text-gray-500">UPI</div>
                  <div className="font-semibold text-purple-700">
                    ₹{expectedUPI.toLocaleString()}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-white rounded-lg p-3">
                <CreditCard className="w-4 h-4 text-blue-600" />
                <div>
                  <div className="text-xs text-gray-500">Card</div>
                  <div className="font-semibold text-blue-700">
                    ₹{expectedCard.toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Cash Section - Only show if there's expected cash */}
          {expectedCash > 0 ? (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Banknote className="w-4 h-4 text-green-600" />
                  <label className="text-sm font-medium text-gray-700">
                    Cash Count <span className="text-red-500">*</span>
                  </label>
                </div>
                <div className="text-sm text-gray-500">
                  Expected:{" "}
                  <span className="font-semibold text-green-700">
                    ₹{expectedCash.toLocaleString()}
                  </span>
                </div>
              </div>
              <DenominationGrid
                denomination={denomination}
                onChange={handleDenominationChange}
                ringColor="ring-red-500"
              />

              {/* Cash Variance */}
              {hasDenominationEntered() && (
                <div
                  className={`mt-3 p-4 rounded-xl ${
                    cashVariance === 0
                      ? "bg-green-50 border border-green-200"
                      : cashVariance > 0
                        ? "bg-blue-50 border border-blue-200"
                        : "bg-red-50 border border-red-200"
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-sm text-gray-600">
                        Actual Cash Count
                      </div>
                      <div className="text-xl font-bold">
                        ₹{actualCash.toLocaleString()}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1">
                        {cashVariance !== 0 && (
                          <AlertTriangle className="w-4 h-4" />
                        )}
                        <span
                          className={`font-semibold ${
                            cashVariance === 0
                              ? "text-green-700"
                              : cashVariance > 0
                                ? "text-blue-700"
                                : "text-red-700"
                          }`}
                        >
                          {cashVariance === 0
                            ? "✓ Exact Match!"
                            : cashVariance > 0
                              ? `+₹${cashVariance.toLocaleString()} Surplus`
                              : `-₹${Math.abs(cashVariance).toLocaleString()} Shortage`}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <div className="flex items-center gap-2 text-gray-600">
                <Banknote className="w-4 h-4" />
                <span className="text-sm">
                  No cash transactions in this session
                </span>
              </div>
            </div>
          )}

          {/* Notes */}
          {cashVariance !== 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Variance approval request ID
              </label>
              <input
                type="text"
                value={approvalRequestId}
                onChange={(event) => setApprovalRequestId(event.target.value)}
                placeholder="Required when the server approval threshold is met"
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any remarks about variance, etc..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 resize-none text-sm"
            />
          </div>
        </div>

        {/* Footer - Fixed */}
        <div className="px-5 py-4 border-t border-gray-100 bg-white flex-shrink-0">
          <form onSubmit={handleSubmit} className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                isLoading ||
                submitting ||
                (expectedCash > 0 && !hasDenominationEntered()) ||
                (action === "HANDOVER" && !handoverTo)
              }
              className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {(isLoading || submitting) && (
                <Loader2 className="w-4 h-4 animate-spin" />
              )}
              {action === "HANDOVER" ? "Handover Drawer" : "Close Session"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

interface SwitchWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmClose: () => void;
  switchType: "store" | "counter";
  targetName?: string;
}

export function SwitchWarningModal({
  isOpen,
  onClose,
  onConfirmClose,
  switchType,
  targetName,
}: SwitchWarningModalProps) {
  const { activeSession } = useBillingSessionStore();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                Active Session Found
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Close current session to switch {switchType}
              </p>
            </div>
          </div>
        </div>
        <div className="p-6">
          <p className="text-gray-600 mb-4">
            You have an active billing session at{" "}
            <span className="font-semibold">
              {activeSession?.counter?.name}
            </span>
            . To switch to{" "}
            <span className="font-semibold">
              {targetName || `another ${switchType}`}
            </span>
            , you must first close your current session.
          </p>
          <div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-500">
            Current Session:{" "}
            <span className="font-medium text-gray-900">
              #{activeSession?.sessionNumber}
            </span>
            <br />
            Started:{" "}
            {activeSession?.startTime
              ? formatDateTime(activeSession.startTime)
              : "N/A"}
          </div>
        </div>
        <div className="px-6 pb-6 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirmClose}
            className="flex-1 px-4 py-2.5 bg-amber-600 text-white rounded-xl hover:bg-amber-700 font-medium transition-colors"
          >
            Close Session
          </button>
        </div>
      </div>
    </div>
  );
}

interface SessionStatusBannerProps {
  onOpenSession: () => void;
}

export function SessionStatusBanner({
  onOpenSession,
}: SessionStatusBannerProps) {
  const { activeSession } = useBillingSessionStore();

  if (activeSession) {
    return (
      <div className="bg-teal-50 border-b border-teal-100 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          <span className="text-teal-700">
            Session Active:{" "}
            <span className="font-semibold">
              #{activeSession.sessionNumber}
            </span>
            {activeSession.counter?.name && (
              <> • {activeSession.counter.name}</>
            )}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-amber-50 border-b border-amber-100 px-4 py-3 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <span className="text-sm text-amber-700">
          No active billing session. Open a session to enable billing.
        </span>
      </div>
      <button
        onClick={onOpenSession}
        className="px-3 py-1.5 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700 font-medium transition-colors"
      >
        Open Session
      </button>
    </div>
  );
}
